// src/pages/subscription/AddOnDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionAddon, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { AddOnFormDialog } from '@/features/subscription/AddOnFormDialog';
import { formatMoney, formatDate, humanizeEnum } from '@/features/subscription/format';

export const AddOnDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscriptionAddon();
  const { t } = useI18n();
  const [deleting, setDeleting] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const item = store.currentItem as any;

  useEffect(() => {
    if (id) store.GetAddOn(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleDelete = async () => {
    if (!id || !confirm(t('common.confirmDelete'))) return;
    setDeleting(true);
    try {
      await store.DeleteAddOn(id);
      navigate('/subscriptions/addons');
    } catch {
      // Error handled by store
    } finally {
      setDeleting(false);
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/subscriptions/addons" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/subscriptions/addons" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const compatiblePlans: string[] = item.compatible_plan_slugs ?? [];
  const requiredPlans: string[] = item.required_plan_slugs ?? [];
  const features: string[] = item.features ?? [];

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/subscriptions/addons" className="text-muted-foreground hover:text-foreground">Add-Ons</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? id)}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{String(item.name ?? '')}</h1>
            {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{String(item.description ?? '')}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/subscriptions/addons/${id}/purchase`}><Button>Purchase</Button></Link>
          <Button variant="secondary" onClick={() => setShowEdit(true)}>{t('common.edit')}</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? '...' : t('common.delete')}
          </Button>
        </div>
      </div>

      <AddOnFormDialog
        open={showEdit}
        addon={item ?? null}
        onClose={() => setShowEdit(false)}
        onSaved={() => { if (id) store.GetAddOn(id); }}
      />

      {/* Basic Info */}
      <Card>
        <CardHeader><CardTitle>Basic Info</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.name ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.slug ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.description ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Type</dt>
              <dd className="text-sm col-span-2">
                <Badge variant="secondary" size="sm">{humanizeEnum(item.addon_type, 'ADDON_TYPE_')}</Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader><CardTitle>Pricing</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Price</dt>
              <dd className="text-sm text-foreground col-span-2">{formatMoney(item.price_amount, item.currency)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Currency</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.currency ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Billing Cycle</dt>
              <dd className="text-sm text-foreground col-span-2">{humanizeEnum(item.billing_cycle, 'BILLING_CYCLE_')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">One-Time</dt>
              <dd className="text-sm col-span-2">
                {item.is_one_time ? <Badge variant="primary" size="sm">Yes</Badge> : <Badge variant="secondary" size="sm">No</Badge>}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Status */}
      <Card>
        <CardHeader><CardTitle>Status</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Active</dt>
              <dd className="text-sm col-span-2">
                {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Public</dt>
              <dd className="text-sm col-span-2">
                {item.is_public ? <Badge variant="primary" size="sm">Public</Badge> : <Badge variant="secondary" size="sm">Private</Badge>}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Compatibility */}
      <Card>
        <CardHeader><CardTitle>Compatibility</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Compatible Plans</dt>
              <dd className="text-sm text-foreground col-span-2">
                {compatiblePlans.length > 0
                  ? <div className="flex flex-wrap gap-1">{compatiblePlans.map((slug: string) => <Badge key={slug} variant="secondary" size="sm">{slug}</Badge>)}</div>
                  : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Required Plans</dt>
              <dd className="text-sm text-foreground col-span-2">
                {requiredPlans.length > 0
                  ? <div className="flex flex-wrap gap-1">{requiredPlans.map((slug: string) => <Badge key={slug} variant="warning" size="sm">{slug}</Badge>)}</div>
                  : '--'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Features */}
      {features.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Features</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {features.map((feature: string, idx: number) => (
                <li key={idx} className="text-sm text-foreground flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* External */}
      {(item.stripe_product_id || item.stripe_price_id) && (
        <Card>
          <CardHeader><CardTitle>External Integrations</CardTitle></CardHeader>
          <CardContent>
            <dl className="divide-y divide-border">
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Stripe Product ID</dt>
                <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.stripe_product_id ?? '--')}</dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Stripe Price ID</dt>
                <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.stripe_price_id ?? '--')}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Timestamps */}
      <Card>
        <CardHeader><CardTitle>Timestamps</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.updated_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Archived</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.archived_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default AddOnDetailPage;
